import { useState, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  Tabs,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  DeleteIcon,
  LinkIcon,
  ViewIcon,
} from '@chakra-ui/icons';

import Card from 'components/card/Card';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { APISubmissionPolicyData } from 'types/api.types';
import {
  POLICY_METADATA_STATUS_FAILED,
  RUNNER_COMMERCIAL_AUTO,
  RUNNER_COMMERCIAL_PROPERTY_CASUALTY,
  RUNNER_LEGACY_AUTO,
  RUNNER_MC_LIFE,
  RUNNER_VALIDATE_AUTO_QUOTE,
  RUNNER_VALIDATE_HOME_QUOTE,
  WIP_STATUS_SIGNING_AWAITING_BROKER,
} from 'variables/constants';

interface PolicyTabsProps {
  policies: APISubmissionPolicyData[];
  selectedTabIndex: number;
  isPolicyLinked: (uuid: string) => boolean;
  onTabSelect: (policy: APISubmissionPolicyData) => void;
  onUnlink: (policyUuid: string) => Promise<void>;
  onDeleteClick: (
    policy: APISubmissionPolicyData,
    index: number,
    e: React.MouseEvent,
  ) => void;
  onAddClick: () => void;
  onLinkClick: () => void;
  onBinderOverviewClick: (policyUuid: string) => void;
  isUnlinking: boolean;
  isDeleting: boolean;
  hasGroupedPolicies: boolean;
  canLinkBinders: boolean;
}

const getPolicyTypeDisplay = (policy: APISubmissionPolicyData): string => {
  if (policy.runner === RUNNER_MC_LIFE) {
    return 'Life';
  }
  if (policy.type === 'unknown' || /^\d+$/.test(policy.type)) {
    if (policy.runner === RUNNER_VALIDATE_AUTO_QUOTE) {
      return 'Auto';
    }
    if (policy.runner === RUNNER_VALIDATE_HOME_QUOTE) {
      return 'Home';
    }
    return 'Unknown';
  }
  return policy.type;
};

const canModifyPolicy = (policy: APISubmissionPolicyData): boolean => {
  if (!policy.wip_status) return true;
  return policy.wip_status < WIP_STATUS_SIGNING_AWAITING_BROKER;
};

export const getPolicyRoute = (
  policy: APISubmissionPolicyData,
  submissionId: string,
) => {
  const base = `/admin/policies/submission/${submissionId}/${policy.uuid}`;

  if (
    policy.runner === RUNNER_VALIDATE_AUTO_QUOTE ||
    policy.runner === RUNNER_VALIDATE_HOME_QUOTE
  ) {
    return `${base}/specialty`;
  }
  if (
    policy.runner === RUNNER_COMMERCIAL_PROPERTY_CASUALTY ||
    policy.runner === RUNNER_COMMERCIAL_AUTO
  ) {
    return `${base}/commercial`;
  }
  if (policy.runner === RUNNER_LEGACY_AUTO) {
    return `${base}/legacy-auto`;
  }
  if (policy.runner === RUNNER_MC_LIFE) {
    return `${base}/life`;
  }
  if (
    !policy.metadata ||
    policy.metadata.status === POLICY_METADATA_STATUS_FAILED
  ) {
    return `${base}/validations`;
  }

  return `${base}/personal`;
};

const PolicyTabs = ({
  policies,
  selectedTabIndex,
  isPolicyLinked,
  onTabSelect,
  onUnlink,
  onDeleteClick,
  onAddClick,
  onLinkClick,
  onBinderOverviewClick,
  isUnlinking,
  isDeleting,
  hasGroupedPolicies,
  canLinkBinders,
}: PolicyTabsProps) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMultiBinderLinkingEnabled = useFeatureFlag(
    'enable-multi-binder-linking',
  );
  const [policyToUnlink, setPolicyToUnlink] = useState<{
    uuid: string;
    type: string;
    index: number;
  } | null>(null);

  const handleUnlinkClick = (
    policy: APISubmissionPolicyData,
    index: number,
  ) => {
    setPolicyToUnlink({
      uuid: policy.uuid,
      type: policy.type,
      index: index + 1,
    });
    onOpen();
  };

  const handleConfirmUnlink = async () => {
    if (policyToUnlink) {
      await onUnlink(policyToUnlink.uuid);
      onClose();
      setPolicyToUnlink(null);
    }
  };

  const handleCancelUnlink = () => {
    onClose();
    setPolicyToUnlink(null);
  };

  return (
    <>
      <Card mb="20px">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md" color={textColor}>
            Submissions
          </Heading>
          <Flex gap={2}>
            {selectedTabIndex >= 0 &&
              policies[selectedTabIndex] &&
              policies[selectedTabIndex].metadata && (
                <Button
                  size="sm"
                  leftIcon={<ViewIcon />}
                  colorScheme="teal"
                  variant="outline"
                  onClick={() =>
                    onBinderOverviewClick(policies[selectedTabIndex].uuid)
                  }
                >
                  Binder Overview
                </Button>
              )}
            {isMultiBinderLinkingEnabled && canLinkBinders && (
              <Button
                size="sm"
                leftIcon={<LinkIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={onLinkClick}
              >
                {hasGroupedPolicies ? 'Add to Group' : 'Link Binders'}
              </Button>
            )}
          </Flex>
        </Flex>

        <Tabs
          index={selectedTabIndex >= 0 ? selectedTabIndex : undefined}
          variant="enclosed"
        >
          <TabList>
            {policies.map((policy, index) => {
              const isLinked = isPolicyLinked(policy.uuid);
              return (
                <Tab
                  key={policy.uuid}
                  onClick={() => onTabSelect(policy)}
                  _focus={{ boxShadow: 'none' }}
                  position="relative"
                  bg={isLinked ? 'blue.50' : undefined}
                  borderColor={isLinked ? 'blue.200' : undefined}
                  _selected={{
                    bg: isLinked ? 'blue.100' : 'white',
                    borderColor: isLinked ? 'blue.300' : 'gray.200',
                    borderBottomColor: isLinked ? 'blue.100' : 'white',
                  }}
                >
                  <Flex align="center" gap={2} width="100%">
                    {isLinked && (
                      <Icon as={LinkIcon} boxSize={3} color="blue.500" />
                    )}
                    <Text>
                      {policy.runner === RUNNER_MC_LIFE && policy.life_product
                        ? `${policy.life_product} ${index + 1}`
                        : `${getPolicyTypeDisplay(policy)} ${index + 1}`}
                      {policy.is_loa ? ' (LOA)' : ''}
                    </Text>
                    {isLinked ? (
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="Tab actions"
                          icon={<ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          ml="auto"
                          onClick={(e) => e.stopPropagation()}
                          _hover={{ bg: 'blue.200' }}
                        />
                        <MenuList
                          minW="150px"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MenuItem
                            icon={<LinkIcon />}
                            onClick={() => handleUnlinkClick(policy, index)}
                            isDisabled={!canModifyPolicy(policy) || isUnlinking}
                          >
                            Unlink
                          </MenuItem>
                          <MenuItem
                            icon={<DeleteIcon />}
                            color="red.500"
                            onClick={(e) => onDeleteClick(policy, index, e)}
                            isDisabled={!canModifyPolicy(policy) || isDeleting}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    ) : (
                      <IconButton
                        aria-label="Delete policy"
                        icon={<DeleteIcon />}
                        size="xs"
                        variant="ghost"
                        ml="auto"
                        color="red.500"
                        onClick={(e) => onDeleteClick(policy, index, e)}
                        isDisabled={!canModifyPolicy(policy) || isDeleting}
                        _hover={{ bg: 'gray.100' }}
                      />
                    )}
                  </Flex>
                </Tab>
              );
            })}
            <Tab onClick={onAddClick} _focus={{ boxShadow: 'none' }}>
              <Flex align="center" gap={2}>
                <Text>+ Add Binder</Text>
              </Flex>
            </Tab>
          </TabList>
        </Tabs>
      </Card>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelUnlink}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Unlink Binder
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to unlink{' '}
              <strong>
                {policyToUnlink?.type} {policyToUnlink?.index}
              </strong>
              ?
              <Text mt={3} color="orange.600" fontSize="sm">
                This action may affect Multi-Line or Multi-Vehicle discounts.
                Review and remove discount if necessary.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancelUnlink}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleConfirmUnlink}
                ml={3}
                isLoading={isUnlinking}
              >
                Unlink
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default PolicyTabs;
